/* eslint-disable arrow-body-style */

import { CommonToast } from "@src/common/CommonToast";
import { InvocaPhoneNumber } from "@src/common/InvocaPhoneNumber/InvocaPhoneNumber";
import { LoadingOverlay } from "@src/common/LoadingOverlay";
import { SpecialityMatchTypeToggle } from "@src/common/SpecialityMatchTypeToggle";
import { FormSpacing } from "@src/common/styled-components";
import { UpdateAgeModal } from "@src/common/UpdateAgeModal";
import {
  AgeRangeOptions,
  AllServiceTypeRequiredReasonForVisitOptions,
  APP_ROUTES,
  CareFormatOptions,
  CLINICIAN_TYPE,
  FilteredSpecialistOptions,
  LegacyFilteredSpecialistOptions,
  PaymentMethodOptions,
  ServiceTypeOptions,
  SessionTypeOptions,
} from "@src/constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { type SharedData, useApplicationContext } from "@src/context/ApplicationContext";
import { useCanvasContext } from "@src/context/CanvasContext";
import { useFeatureFlags } from "@src/context/FeatureFlagContext";
import { useFeatureExposure } from "@src/context/FeatureManifestContext";
import { useTimerContext } from "@src/context/TimerContext";
import { useCanvasPortalScrollFix } from "@src/hooks/useCanvasPortalScrollFix";
import { useEntryRouteBootstrap } from "@src/hooks/useEntryRouteBootstrap";
import { useExclusiveAction } from "@src/hooks/useExclusiveAction";
import { useIdleFormFlush } from "@src/hooks/useIdleFormFlush";
import { useNavigateWithUTM } from "@src/hooks/useNavigateWithUTM";
import { useReasonsForVisit } from "@src/hooks/useReasonsForVisit";
import { useReleaseFrozenAppointmentOnMount } from "@src/hooks/useReleaseFrozenAppointmentOnMount";
import { useResolvedPageLabels } from "@src/hooks/useResolvedPageLabels";
import { buildOutboundPayload, CANVAS_OUTBOUND_EVENT_NAME } from "@src/services/canvas/outbound";
import { ensureSelfPayPackageLookup } from "@src/services/canvas/prefill/ensureSelfPayPackageLookup";
import { useCanvasPrefillSync } from "@src/services/canvas/prefill/useCanvasPrefillSync";
import ClientLogsService from "@src/services/client-logs.service";
import { buildProviderSearchRequest, getProviderList } from "@src/services/provider-search.service";
import { navigateToContactUs } from "@src/utils/contact-us-navigation";
import logger from "@src/utils/logger";
import { mapProviderList } from "@src/utils/mapping-utils";
import { filterProvidersByRequestedModality } from "@src/utils/provider-modality-filter";
import { retryApiCall } from "@src/utils/retry-api-call";
import {
  canRoleBypassGuardrails,
  canRoleOverrideInsuranceExclusion,
} from "@src/utils/role-guardrails";
import { isUnder12AgeRange, isUnder18AgeRange } from "@src/utils/scheduling-metadata";
import {
  normalizeSessionTypeValue,
  toSessionTypeValues,
} from "@src/utils/session-type-normalization";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Card } from "@uhg-abyss/web/ui/Card";
import { IconSymbol } from "@uhg-abyss/web/ui/IconSymbol";
import { SelectInput } from "@uhg-abyss/web/ui/SelectInput";
import { TextInput } from "@uhg-abyss/web/ui/TextInput";
import { Tooltip } from "@uhg-abyss/web/ui/Tooltip";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { consumeAgeRangeResetConfirmation } from "./ageRangeResetConfirmationSignal";
import { FormFooter } from "./FormFooter";
import {
  hasUsableAddress,
  validateGetStartedField,
  validateGetStartedForm,
} from "./GetStartedFormValidation";
import { useAddressPrefill } from "./hooks/useAddressPrefill";
import { useInsurancePackages } from "./hooks/useInsurancePackages";
import { useLocalCenterScheduling } from "./hooks/useLocalCenterScheduling";
import { useTimezone } from "./hooks/useTimezone";
import {
  deriveEnrollmentCategoryOptions,
  filterInsurancePackagesByEnrollmentCategory,
  getEnrollmentCategoryOptionForInsurancePackage,
  getInsurancePackageDisplayName,
} from "./insurancePackageCategories";
import { LocalCenterProviderUnavailableModal } from "./LocalCenterProviderUnavailableModal";
import { filterBySearch, moveSelectedToTop, toSelectedPolicyValues } from "./SelectOptionOrdering";

export { hasUsableAddress, isResolvedAddressForZip } from "./GetStartedFormValidation";
export { getAddressSearchErrorMessage } from "./hooks/useAddressPrefill";

const sharedSelectInputStyle = {
  "abyss-select-input-container-wrapper": {
    ".abyss-select-input-container": {
      ".abyss-select-input-selection-container": {},
    },
  },
  "abyss-select-input-root": {
    ".abyss-select-input-wrapper": {
      ".abyss-select-input-label": {},
      ".abyss-select-input-menu-search-input": {},
    },
    ".abyss-portal": {
      overflow: "visible !important",
      position: "relative !important",
    },
  },
};

const SessionReasonGroup = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "$core.spacing.300",
});

const InsurancePackageSelectionGroup = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "$core.spacing.300",
});

const AgeRangeSelectWrapper = styled("div", {
  ".abyss-select-input-label": {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "baseline",
  },
  ".abyss-label-content": {
    lineHeight: "1",
    whiteSpace: "normal",
  },
  ".abyss-label-required-indicator": {
    display: "none",
  },
});

const AgeRangeGuidanceIcon = styled("div", {
  display: "inline-flex",
  alignItems: "center",
  padding: "0",
  border: "0",
  background: "transparent",
  color: "$app.color.text.link.rest.primary",
  cursor: "pointer",
});

const AgeRangeGuidance = styled("span", {
  alignItems: "center",
  display: "inline-flex",
  lineHeight: "$core.lineHeight.300",
  gap: "$core.spacing.100",
});

const AgeRangeRequiredIndicator = styled("span", {
  color: "$app.color.text.status.error",
});

const DEFAULT_SPECIALITY_MATCH_TYPE: NonNullable<SharedData["specialityMatchType"]> = "or";

const getClinicianTypeForService = (
  serviceType?: string,
): NonNullable<SharedData["providerType"]> => {
  return serviceType === ServiceTypeOptions[0] || !serviceType
    ? CLINICIAN_TYPE.THERAPIST
    : CLINICIAN_TYPE.PRESCRIBER;
};

const isServiceTypeRequiredForSpecialities = (
  specialities?: string[],
  requiredSpecialities: ReadonlyArray<string> = AllServiceTypeRequiredReasonForVisitOptions,
): boolean => {
  return (
    Array.isArray(specialities) &&
    specialities.some((speciality) => requiredSpecialities.includes(speciality))
  );
};

const isTherapyServiceType = (serviceType?: string): boolean => {
  return !serviceType || serviceType === ServiceTypeOptions[0];
};

const shouldRequireCareFormat = (
  serviceType: string | undefined,
  specialities?: string[],
  requiredSpecialities?: ReadonlyArray<string>,
): boolean => {
  return (
    serviceType === ServiceTypeOptions[0] &&
    isServiceTypeRequiredForSpecialities(specialities, requiredSpecialities)
  );
};

const resolveCareFormatForPersistence = (
  shouldShowCareFormat: boolean,
  selectedCareFormat: string,
  serviceType?: string,
): string => {
  if (shouldShowCareFormat) {
    return selectedCareFormat;
  }
  return isTherapyServiceType(serviceType) ? "Individual" : "";
};

export const resolveProviderSearchAddress = (formAddress: any, searchedAddress: any): any =>
  formAddress || searchedAddress;

const shouldRestoreGetStartedCareFormat = (data: SharedData): boolean => {
  return (
    data.requiresGetStartedServiceType === true &&
    isServiceTypeRequiredForSpecialities(data.specialities)
  );
};

const normalizeSessionTypeToArray = (sessionType?: SharedData["sessionType"]): string[] => {
  if (!sessionType || sessionType === "no preference") {
    return [];
  }

  const normalizedValues = toSessionTypeValues(sessionType)
    .map(normalizeSessionTypeValue)
    .filter(Boolean);
  return Array.from(new Set(normalizedValues));
};

const areStringArraysEqual = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => value === right[index]);
};

const hasExplicitSessionTypeSelection = (sessionType?: SharedData["sessionType"]): boolean => {
  const sessionTypeArray = toSessionTypeValues(sessionType);

  return sessionTypeArray.some((type) => {
    const normalizedType = type.trim().toLowerCase();
    return normalizedType !== "" && normalizedType !== "no preference";
  });
};

const resolveSessionTypeForPersistence = (
  sessionTypeOptions: string[],
): SharedData["sessionType"] => {
  if (!Array.isArray(sessionTypeOptions) || sessionTypeOptions.length === 0) {
    return "no preference";
  }

  if (sessionTypeOptions.length === 1) {
    return sessionTypeOptions[0];
  }

  return sessionTypeOptions;
};

const intialFormState = (data: any) => ({
  zip: data.zip || "",
  selectAge: data.ageRange || "",
  selectPlan: data.paymentMethod || "",
  selectedEnrollmentCategory: null,
  selectedPolicy: data.selectedPolicy || null,
  insurancePolicy: data.insurancePolicy || {},
  specialities: Array.isArray(data.specialities) ? data.specialities : [],
  providerGender: data.providerGender || "",
  sessionType: normalizeSessionTypeToArray(data.sessionType),
  careFormat: shouldRestoreGetStartedCareFormat(data) ? data.careFormat || "" : "",
  typeOfService: data.typeOfService || "",
  address: data.address || null,
});

// ─── Module-level helpers ─────────────────────────────────────────────────────
// Extracted from handleSubmit to keep retryApiCall callbacks shallow (≤ 4 levels).
class ProviderSearchTimeoutError extends Error {
  readonly isTimeout = true as const;
  constructor(message: string) {
    super(message);
    this.name = "ProviderSearchTimeoutError";
  }
}

const isProviderSearchTimeout = (error: unknown): error is ProviderSearchTimeoutError =>
  error instanceof ProviderSearchTimeoutError ||
  (typeof error === "object" &&
    error !== null &&
    (error as Record<string, unknown>).isTimeout === true);

const fetchProviderListOnce = async (
  requestedParams: Parameters<typeof getProviderList>[0],
): Promise<any[]> => {
  const providerResponse = await getProviderList(requestedParams);
  if (!providerResponse.error) {
    return providerResponse.message || [];
  }
  const errorMsg = String(providerResponse.error);
  const isTimeoutError =
    errorMsg.toLowerCase().includes("upstream server is timing out") ||
    errorMsg.toLowerCase().includes("timeout");
  if (isTimeoutError) {
    throw new ProviderSearchTimeoutError(errorMsg);
  }
  throw new Error(errorMsg);
};

export const GetStartedForm = () => {
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const { data, setData, markRouteAsComplete, clearAppointmentSelection } = useApplicationContext(); // Access Application context
  const { stopTimer } = useTimerContext();
  const {
    isInSalesforce: canvasIsInSalesforce,
    publishToSalesforce,
    isLoading: canvasIsLoading,
  } = useCanvasContext();
  const {
    FEATURE_ENROLLMENT_CATEGORY_PACKAGE_FILTER,
    FEATURE_PRESCRIBER,
    FEATURE_SCHEDULING_CONSOLIDATION,
    FEATURE_UPDATED_REASON_FOR_VISIT,
    FEATURE_REASONS_FOR_VISIT_API,
  } = useFeatureFlags();
  const { reasonsForVisit, error: reasonsForVisitError } = useReasonsForVisit();
  const legacySpecialistOptions = FEATURE_UPDATED_REASON_FOR_VISIT
    ? FilteredSpecialistOptions
    : LegacyFilteredSpecialistOptions;
  const specialistOptions = FEATURE_REASONS_FOR_VISIT_API
    ? reasonsForVisit.map((reason) => reason.display)
    : legacySpecialistOptions;
  const requiresServiceTypeSpecialities: ReadonlyArray<string> =
    AllServiceTypeRequiredReasonForVisitOptions;
  const isPatientNavigator = canRoleBypassGuardrails(data.userRole);
  const isEnrollmentCategoryPackageFilterEnabled = Boolean(
    FEATURE_ENROLLMENT_CATEGORY_PACKAGE_FILTER,
  );
  const isSchedulingConsolidationEnabled = Boolean(FEATURE_SCHEDULING_CONSOLIDATION);
  const [value, setValue] = useState(() => intialFormState(data));
  const [errors, setErrors] = useState({
    typeOfService: "",
    careFormat: "",
    sessionType: "",
    zip: "",
    selectAge: "",
    selectPlan: "",
    selectedEnrollmentCategory: "",
    selectedPolicy: "",
  });
  const [showUpdateAgeModal, setShowUpdateAgeModal] = useState(false);
  const [showAgeRangeResetConfirmation, setShowAgeRangeResetConfirmation] = useState(() =>
    consumeAgeRangeResetConfirmation(),
  );

  const hasFrozenAppointment = Boolean(data.appointmentDetails?.appointmentIds?.length);

  useReleaseFrozenAppointmentOnMount({
    userUuid: data.userUuid,
    hasFrozenAppointment,
    logContext: "GetStartedForm.unfreezeExistingAppointment",
    onReleased: clearAppointmentSelection,
    onSettled: stopTimer,
  });

  const [isChecked, setIsChecked] = useState(data.isGetStartedChecked || false);
  const [showCheckboxError, setShowCheckboxError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [specialtyInputValue, setSpecialtyInputValue] = useState("");
  const [specialtyResetKey, setSpecialtyResetKey] = useState(0);
  const [specialityMatchType, setSpecialityMatchType] = useState<
    NonNullable<SharedData["specialityMatchType"]>
  >(data.specialityMatchType ?? DEFAULT_SPECIALITY_MATCH_TYPE);
  const [policyInputValue, setPolicyInputValue] = useState("");
  const [policyResetKey, setPolicyResetKey] = useState(0);
  const [enrollmentCategoryResetKey, setEnrollmentCategoryResetKey] = useState(0);
  const [insuranceFetchKey, setInsuranceFetchKey] = useState(0);
  const specialtyContainerRef = useRef<HTMLDivElement>(null);
  const policyContainerRef = useRef<HTMLDivElement>(null);
  const proceedAsInPersonRef = useRef(false);
  const navigate = useNavigateWithUTM();
  const { runExclusive } = useExclusiveAction("get-started:next");
  const navTimeoutRef = useRef<number | null>(null);
  const selectedProvider = data.selectedProvider;

  // ─── Draft-save on "I'm here" ─────────────────────────────────
  // Persists the current partial form state to ApplicationContext without
  // running validation. Called by IdleTimerContext when the user clicks
  // "I'm here" on the idle warning modal.
  const saveToContext = useCallback(() => {
    const draftSessionType =
      value.sessionType.length > 0
        ? resolveSessionTypeForPersistence(value.sessionType)
        : isSchedulingConsolidationEnabled
          ? []
          : undefined;

    setData("zip", value.zip || undefined);
    setData("ageRange", value.selectAge || undefined);
    setData("typeOfService", value.typeOfService || undefined);
    setData("paymentMethod", value.selectPlan || undefined);
    setData("selectedPolicy", value.selectedPolicy || null);
    setData("specialities", value.specialities);
    setData("specialityMatchType", specialityMatchType);
    setData("providerGender", value.providerGender || undefined);
    setData("sessionType", draftSessionType);
    setData("careFormat", value.careFormat || undefined);
    setData("address", value.address || undefined);
  }, [isSchedulingConsolidationEnabled, setData, specialityMatchType, value]);

  useIdleFormFlush(saveToContext);

  // Reset search inputValue when user clicks outside the select container.
  // Abyss clears the visible search text internally on outside click but does
  // not fire onInputChange(""), leaving our state stale and the filtered list
  // reduced on next open.
  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      if (
        specialtyContainerRef.current &&
        !specialtyContainerRef.current.contains(e.target as Node)
      ) {
        setSpecialtyInputValue("");
      }
      if (policyContainerRef.current && !policyContainerRef.current.contains(e.target as Node)) {
        setPolicyInputValue("");
      }
    };
    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, []);

  useCanvasPortalScrollFix(canvasIsInSalesforce);

  const isFeaturePrescriberEnabled = Boolean(FEATURE_PRESCRIBER);
  const ageRangeOptions = AgeRangeOptions;
  const { resolvedUserRole } = useEntryRouteBootstrap({
    canEnsureUuid: !data.userUuid || Boolean(data.isReturningFromContactFlow),
    forceRegenerateUuid: Boolean(data.isReturningFromContactFlow),
    onUserUuidCreated: () => {
      if (data.isReturningFromContactFlow) {
        setData("isReturningFromContactFlow", false);
      }

      logger.info({
        text: "Generated new userUuid for Get Started Form",
        location: "GetStartedForm",
      });
    },
    digitalInfoLogLocation: "GetStartedForm",
  });

  const labelAge = useMemo(() => {
    if (!value.selectAge) {
      return undefined;
    }

    return isUnder18AgeRange(value.selectAge) ? 0 : 18;
  }, [value.selectAge]);

  const labels = useResolvedPageLabels("getStartedForm", { age: labelAge });
  const selectedServiceType = isFeaturePrescriberEnabled
    ? value.typeOfService
    : ServiceTypeOptions[0];
  const shouldShowServiceType = shouldRequireCareFormat(
    selectedServiceType,
    value.specialities,
    requiresServiceTypeSpecialities,
  );

  useEffect(() => {
    const hasInput = Boolean(
      value.zip || value.selectAge || value.selectPlan || value.typeOfService,
    );
    setData("hasGetStartedFormInput", hasInput);
  }, [value.zip, value.selectAge, value.selectPlan, value.typeOfService]);

  useEffect(() => {
    // New Get Started session should not inherit prior screener-origin navigation state.
    setData("fromScreener", false);
    setData("languages", "");
    const shouldRestoreServiceType = shouldRestoreGetStartedCareFormat(data);
    const isTherapyService = isTherapyServiceType(data.typeOfService);
    const restoredCareFormat =
      shouldRestoreServiceType && isTherapyService ? data.careFormat || "" : "";
    let careFormatToSet = "";

    if (shouldRestoreServiceType) {
      careFormatToSet = restoredCareFormat;
    } else if (isTherapyService) {
      careFormatToSet = "Individual";
    }
    setData("careFormat", careFormatToSet);
    setValue((prev) => ({
      ...prev,
      careFormat: restoredCareFormat,
    }));
    setData("providerGender", "");
    // Under consolidation, preserve an explicit persisted selection when present.
    // Otherwise, reset to an unselected state so users make an explicit choice.
    const shouldPreservePersistedSessionType =
      isSchedulingConsolidationEnabled && hasExplicitSessionTypeSelection(data.sessionType);

    if (shouldPreservePersistedSessionType) {
      const normalizedPersistedSessionType = normalizeSessionTypeToArray(data.sessionType);
      setData("sessionType", normalizedPersistedSessionType);
      setValue((prev) => ({ ...prev, sessionType: normalizedPersistedSessionType }));
    } else {
      setData("sessionType", isSchedulingConsolidationEnabled ? [] : "no preference");
      if (isSchedulingConsolidationEnabled) {
        setValue((prev) => ({ ...prev, sessionType: [] }));
      }
    }
    setData("providerRadius", "");
    if (data.selectedDate || data.selectedDepartment) {
      logger.info({
        text: "Clearing selectedDate and selectedDepartment in ApplicationContext",
        location: "GetStartedForm",
      });
      setData("selectedDate", null);
      setData("selectedDepartment", null);
    }
    return () => {
      if (navTimeoutRef.current) {
        clearTimeout(navTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isSchedulingConsolidationEnabled) {
      return;
    }

    if (!hasExplicitSessionTypeSelection(data.sessionType)) {
      const isContextAlreadyEmptyArray =
        Array.isArray(data.sessionType) && data.sessionType.length === 0;

      if (!isContextAlreadyEmptyArray) {
        setData("sessionType", []);
      }
    }

    const normalizedSessionType = normalizeSessionTypeToArray(data.sessionType);

    setValue((prev) => {
      if (areStringArraysEqual(prev.sessionType, normalizedSessionType)) {
        return prev;
      }

      return {
        ...prev,
        sessionType: normalizedSessionType,
      };
    });
  }, [isSchedulingConsolidationEnabled, data.sessionType, setData]);

  useEffect(() => {
    if (value.selectPlan === "self_pay") {
      setValue((prevValue) => ({
        ...prevValue,
        selectedPolicy: null,
        insurancePolicy: {},
      }));
      setErrors((prevErrors) => ({ ...prevErrors, selectedPolicy: "" }));
    }
  }, [value.selectPlan]);

  useCanvasPrefillSync(setValue, {
    zip: (d) => d.zip,
    selectAge: (d) => d.ageRange,
    selectPlan: (d) => d.paymentMethod,
    selectedPolicy: (d) => d.selectedPolicy,
    insurancePolicy: (d) => d.insurancePolicy || {},
    specialities: (d) => (Array.isArray(d.specialities) ? d.specialities : undefined),
    address: (d) => d.address,
  });

  const handleUpdateAgeScenario = () => {
    setIsLoading(false);
    setShowUpdateAgeModal(true);
  };

  const handleUpdateAgeModalClose = () => {
    setShowUpdateAgeModal(false);
  };

  const handleUpdateAgeModalConfirm = () => {
    proceedAsInPersonRef.current = true;
    setData("isUnder12InPersonProceed", true);
    // Context uses the appropriate type; form state is always string[]
    setData("sessionType", isSchedulingConsolidationEnabled ? ["In-person"] : "In-person");
    setValue((prev) => ({
      ...prev,
      sessionType: ["In-person"],
    }));
    setShowUpdateAgeModal(false);
    // Re-trigger provider search with in-person modality
    setTimeout(() => {
      setIsLoading(true);
      handleSubmit({ preventDefault: () => {} } as any);
    }, 0);
  };

  const resetPoliciesRef = useRef<() => void>(() => {});
  const { addressData, resolvedZip, handleZipChange } = useAddressPrefill({
    data,
    valueZip: value.zip,
    resolvedUserRole,
    labels,
    setValue,
    setData,
    setErrors,
    resetPolicies: () => resetPoliciesRef.current(),
    setInsuranceFetchKey,
  });

  const addressContextId = addressData?.context_id;
  const addressStateCode = addressData?.state_code;

  const { insurancePolicies, policyError, resetPolicies, packageLookupCompleted } =
    useInsurancePackages(
      data.userUuid,
      addressContextId,
      addressStateCode,
      value.selectPlan,
      data?.isReferrerFlow === true,
      data?.selectedPartnerFilteredInsurancePackages,
      insuranceFetchKey,
    );
  resetPoliciesRef.current = resetPolicies;

  const enrollmentCategoryOptions = useMemo(
    () =>
      isEnrollmentCategoryPackageFilterEnabled
        ? deriveEnrollmentCategoryOptions(insurancePolicies)
        : [],
    [insurancePolicies, isEnrollmentCategoryPackageFilterEnabled],
  );
  const filteredInsurancePolicies = useMemo(() => {
    if (!isEnrollmentCategoryPackageFilterEnabled) {
      return insurancePolicies;
    }

    return filterInsurancePackagesByEnrollmentCategory(
      insurancePolicies,
      value.selectedEnrollmentCategory,
      enrollmentCategoryOptions,
    );
  }, [
    enrollmentCategoryOptions,
    insurancePolicies,
    isEnrollmentCategoryPackageFilterEnabled,
    value.selectedEnrollmentCategory,
  ]);

  useFeatureExposure(
    "ENROLLMENT_CATEGORY_PACKAGE_FILTER",
    "get-started-insurance-category",
    APP_ROUTES.GET_STARTED,
    isEnrollmentCategoryPackageFilterEnabled,
  );

  // Sync fetch-level insurance error into the form error state.
  useEffect(() => {
    setErrors((prev) => ({ ...prev, selectedPolicy: policyError }));
  }, [policyError]);

  const isEap = useMemo(() => {
    // Stryker disable next-line LogicalOperator,ConditionalExpression -- equivalent mutants: useInsurancePackages always returns [] for non-insurance plans
    if (value.selectPlan !== "insurance" || !value.selectedPolicy) return false;
    const selectedPolicy = filteredInsurancePolicies.find(
      (p) => String(p.id) === String(value.selectedPolicy),
    );
    return typeof selectedPolicy?.name === "string" && /\bEAP\b/i.test(selectedPolicy.name);
  }, [value.selectPlan, value.selectedPolicy, filteredInsurancePolicies]);

  const isRoleOverrideExclusionEnabled = useMemo(() => {
    const selectedPolicy = filteredInsurancePolicies.find(
      (p) => String(p.id) === String(value.selectedPolicy),
    );

    return canRoleOverrideInsuranceExclusion(
      resolvedUserRole,
      selectedPolicy?.roles_allowed_to_override,
    );
  }, [filteredInsurancePolicies, resolvedUserRole, value.selectedPolicy]);

  const selectedInsurancePolicy = useMemo(() => {
    return filteredInsurancePolicies.find((policy) => {
      return String(policy.id) === String(value.selectedPolicy);
    });
  }, [filteredInsurancePolicies, value.selectedPolicy]);
  const {
    showProviderNotFoundModal,
    handleProviderNotFoundSeeMoreProviders,
    handleProviderSearchResult,
  } = useLocalCenterScheduling();
  // - non-insurance (e.g., self-pay),
  // - non-EAP insurance without an excluded policy,
  // - non-EAP insurance with an excluded policy when override is allowed, or
  // - EAP insurance when role-allowed exclusion override is enabled.
  const shouldShowUnder12AgeModal = useMemo(() => {
    // Stryker disable next-line BlockStatement,ConditionalExpression -- non-insurance flows fall through to the same truthy result after form-state normalization
    if (value.selectPlan !== "insurance") {
      return true;
    }

    if (isEap) {
      return isRoleOverrideExclusionEnabled;
    }

    const isExcluded = selectedInsurancePolicy?.exclusion_ind === true;

    return !isExcluded || isRoleOverrideExclusionEnabled;
  }, [isEap, isRoleOverrideExclusionEnabled, selectedInsurancePolicy, value.selectPlan]);

  useEffect(() => {
    if (
      isEnrollmentCategoryPackageFilterEnabled &&
      value.selectedEnrollmentCategory &&
      !enrollmentCategoryOptions.some(
        (categoryOption) => categoryOption.value === value.selectedEnrollmentCategory,
      )
    ) {
      setValue((prevValue) => ({
        ...prevValue,
        selectedEnrollmentCategory: null,
        selectedPolicy: null,
        insurancePolicy: {},
      }));
      setEnrollmentCategoryResetKey((key) => key + 1);
      setPolicyResetKey((key) => key + 1);
    }
  }, [
    enrollmentCategoryOptions,
    isEnrollmentCategoryPackageFilterEnabled,
    value.selectedEnrollmentCategory,
  ]);

  useEffect(() => {
    if (
      !isEnrollmentCategoryPackageFilterEnabled ||
      value.selectPlan !== "insurance" ||
      value.selectedEnrollmentCategory ||
      !value.selectedPolicy
    ) {
      return;
    }

    const selectedPolicy = insurancePolicies.find(
      (insurancePolicy) => String(insurancePolicy.id) === String(value.selectedPolicy),
    );
    if (!selectedPolicy) {
      if (insurancePolicies.length > 0) {
        setValue((prevValue) => ({
          ...prevValue,
          selectedPolicy: null,
          insurancePolicy: {},
        }));
        setPolicyResetKey((key) => key + 1);
      }
      return;
    }

    const categoryOption = getEnrollmentCategoryOptionForInsurancePackage(
      selectedPolicy,
      enrollmentCategoryOptions,
    );
    if (!categoryOption) {
      setValue((prevValue) => ({
        ...prevValue,
        selectedPolicy: null,
        insurancePolicy: {},
      }));
      setPolicyResetKey((key) => key + 1);
      return;
    }

    setValue((prevValue) => ({
      ...prevValue,
      selectedEnrollmentCategory: categoryOption.value,
    }));
  }, [
    enrollmentCategoryOptions,
    insurancePolicies,
    isEnrollmentCategoryPackageFilterEnabled,
    value.selectPlan,
    value.selectedEnrollmentCategory,
    value.selectedPolicy,
  ]);

  useEffect(() => {
    if (
      value.selectPlan !== "insurance" ||
      value.selectedPolicy === null ||
      value.selectedPolicy === undefined ||
      !addressContextId ||
      !addressStateCode ||
      (filteredInsurancePolicies.length === 0 && !policyError)
    ) {
      return;
    }

    const hasSelectedPolicy = filteredInsurancePolicies.some((insurancePolicy) => {
      return String(insurancePolicy.id) === String(value.selectedPolicy);
    });

    if (!hasSelectedPolicy) {
      setValue((prevValue) => ({
        ...prevValue,
        selectedPolicy: null,
        insurancePolicy: {},
      }));
      setPolicyResetKey((key) => key + 1);
    }
  }, [
    addressContextId,
    addressStateCode,
    filteredInsurancePolicies,
    policyError,
    value.selectPlan,
    value.selectedPolicy,
  ]);

  const timezone = useTimezone(
    addressData?.position?.lat,
    addressData?.position?.lon,
    data.userUuid,
  );

  // Persist resolved timezone to ApplicationContext so downstream flows observe it.
  useEffect(() => {
    if (timezone) {
      setData("userTimezone", timezone);
    }
  }, [timezone]);

  const validationOptions = {
    labels,
    isFeaturePrescriberEnabled,
    isEnrollmentCategoryPackageFilterEnabled,
    isSchedulingConsolidationEnabled,
    requiresServiceTypeSpecialities,
    resolvedZip,
    addressData,
    insurancePolicies,
    policyError,
    shouldRequireCareFormat,
  };

  const validateForm = () => {
    const result = validateGetStartedForm(value, validationOptions);
    const selectedPolicyIsAvailable = filteredInsurancePolicies.some(
      (insurancePolicy) => String(insurancePolicy.id) === String(value.selectedPolicy),
    );
    if (value.selectPlan === "insurance" && value.selectedPolicy && !selectedPolicyIsAvailable) {
      result.errors.selectedPolicy = labels.errorInsurancePolicy;
      result.isValid = false;
    }
    setErrors(result.errors as typeof errors);
    return result.isValid;
  };

  const handleChange = async (field: string, fieldValue: any) => {
    if (field === "zip") {
      return handleZipChange(fieldValue);
    }

    if (field === "selectPlan" && fieldValue === "self_pay") {
      setValue((prev) => ({
        ...prev,
        selectedEnrollmentCategory: null,
        selectedPolicy: null,
        insurancePolicy: {},
        selectPlan: fieldValue,
      }));
      setErrors((prev) => ({
        ...prev,
        selectedEnrollmentCategory: "",
        selectedPolicy: "",
        selectPlan: "",
      }));
      return;
    }

    if (field === "selectEnrollmentCategory") {
      setValue((prev) => ({
        ...prev,
        selectedEnrollmentCategory: fieldValue,
        selectedPolicy: null,
        insurancePolicy: {},
      }));
      setErrors((prev) => ({ ...prev, selectedEnrollmentCategory: "" }));
      setPolicyInputValue("");
      setPolicyResetKey((key) => key + 1);
      return;
    }

    if (field === "selectPolicy") {
      setValue((prev) => ({ ...prev, selectedPolicy: fieldValue }));
      setErrors((prev) => ({ ...prev, selectedPolicy: "" }));
      return;
    }

    if (field === "typeOfService" && fieldValue !== value.typeOfService) {
      // Preserve Canvas-prefilled screener answers when switching to a med-mgmt service type
      if (isTherapyServiceType(fieldValue as string)) {
        setData("patientScreenerAnswers", undefined);
      }
      setData("medMgmtInd", undefined);
      setData("fromScreener", false);
    }

    if (field === "selectAge") {
      const isUnder12 = isUnder12AgeRange(fieldValue);
      setValue((prev) => ({
        ...prev,
        selectAge: fieldValue,
        sessionType: isUnder12 ? ["In-person"] : [],
      }));
      if (!isUnder12) {
        proceedAsInPersonRef.current = false;
        setData("isUnder12InPersonProceed", false);
      }
      setErrors((prev) => ({ ...prev, selectAge: "" }));
      return;
    }

    if (field === "specialities") {
      const nextRequiresServiceType = isServiceTypeRequiredForSpecialities(
        fieldValue,
        requiresServiceTypeSpecialities,
      );
      setValue((prev) => ({
        ...prev,
        specialities: fieldValue,
        careFormat:
          isServiceTypeRequiredForSpecialities(
            prev.specialities,
            requiresServiceTypeSpecialities,
          ) && nextRequiresServiceType
            ? prev.careFormat
            : "",
      }));
      setErrors((prev) => ({ ...prev, careFormat: "" }));
      return;
    }

    const newValue = { ...value, [field]: fieldValue };
    setValue(newValue);
    setErrors({
      ...errors,
      [field]: validateGetStartedField(field, fieldValue, newValue, validationOptions),
    });
  };

  // ─── Provider-search callbacks (extracted to reduce nesting depth) ────────────
  const handleErrorToastClose = useCallback(() => {
    ClientLogsService.logWebAction(
      data.userUuid || "unknown",
      "GetStartedForm.CommonToast",
      "Get Started Form Common Toast has closed - Redirecting to Contact Us",
    );
    if (navTimeoutRef.current) {
      clearTimeout(navTimeoutRef.current);
    }
    navigateToContactUs(navigate, data.userUuid, undefined, false);
  }, [data.userUuid, navigate]);

  const handleProviderSearchFailure = useCallback(
    (error: any) => {
      setIsLoading(false);
      if (isProviderSearchTimeout(error)) {
        ClientLogsService.logError(
          data.userUuid || "unknown",
          "GetStartedForm.handleSubmit.retryApiCall.onFailure",
          "Failed to fetch provider list after retries due to timeout error",
          error,
        );
        navigate(APP_ROUTES.ERROR);
        return;
      }
      ClientLogsService.logError(
        data.userUuid || "unknown",
        "GetStartedForm.handleSubmit.retryApiCall.onFailure",
        "Failed to fetch provider list after retries",
        error instanceof Error ? error : new Error(String(error)),
      );
      CommonToast({
        title: "Something went wrong",
        message: (
          <Fragment>
            An unexpected error occurred. Try again, or contact us at{" "}
            <strong>
              <InvocaPhoneNumber />
            </strong>
            .
          </Fragment>
        ),
        toastType: "error",
        toastIcon: "error",
        "data-testid": "test-id-get-started-provider-search-error-toast",
        withCloseButton: true,
        onClose: handleErrorToastClose,
      });
      navTimeoutRef.current = window.setTimeout(() => {
        navigateToContactUs(navigate, data.userUuid, undefined, false);
      }, 5000);
    },
    [data.userUuid, navigate, handleErrorToastClose],
  );

  const handleProviderSearchSuccess = useCallback(
    (providerList: any, requestedModality?: string) => {
      proceedAsInPersonRef.current = false;
      const mappedProviderList = mapProviderList(providerList);
      const filteredProviderList = filterProvidersByRequestedModality(
        mappedProviderList,
        requestedModality,
      );

      setData("providerList", filteredProviderList);
      markRouteAsComplete(APP_ROUTES.GET_STARTED);
      setIsLoading(false);
      setData("fromScreener", false);

      const meetTheTeamFlowHandled = handleProviderSearchResult(
        filteredProviderList,
        selectedProvider ?? null,
      );
      if (meetTheTeamFlowHandled) {
        return;
      }

      if (isFeaturePrescriberEnabled && selectedServiceType !== ServiceTypeOptions[0]) {
        setData("medMgmtInd", true);
        navigate(APP_ROUTES.SCREENER);
        return;
      }
      setData("medMgmtInd", false);
      navigate(APP_ROUTES.FIND_PROVIDER);
    },
    [
      setData,
      markRouteAsComplete,
      navigate,
      isFeaturePrescriberEnabled,
      selectedServiceType,
      handleProviderSearchResult,
      selectedProvider,
    ],
  );

  const shouldNavigateToContact = (policy: any, hasExclusionNote: boolean): boolean => {
    const isExcluded = policy?.exclusion_ind === true && hasExclusionNote;
    const isExcludedNoNote = policy?.exclusion_ind === true && !hasExclusionNote;
    const roleCanOverrideExclusion = canRoleOverrideInsuranceExclusion(
      resolvedUserRole,
      policy?.roles_allowed_to_override,
    );
    return Boolean(
      policy && (isExcluded || isEap || isExcludedNoNote) && !roleCanOverrideExclusion,
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await runExclusive(async () => {
      setIsLoading(true);
      const isFormValid = validateForm();
      const hasSchedulingConsent = isPatientNavigator || isChecked;
      if (!hasSchedulingConsent) {
        setIsLoading(false);
        setShowCheckboxError(true); // Show checkbox error if not checked
      } else {
        setShowCheckboxError(false); // Hide checkbox error if checked
      }

      if (!isFormValid || !hasSchedulingConsent) {
        setIsLoading(false);
        ClientLogsService.logUserError(
          data.userUuid || "unknown",
          "GetStartedForm.handleSubmit",
          "Form has validation errors, unable to proceed",
        );
        return;
      }

      const selectedCareFormat = resolveCareFormatForPersistence(
        shouldShowServiceType,
        value.careFormat,
        selectedServiceType,
      );
      const persistedSessionType = isSchedulingConsolidationEnabled
        ? resolveSessionTypeForPersistence(value.sessionType)
        : "no preference";
      //Save selected values using context
      ClientLogsService.logWebAction(
        data.userUuid || "unknown",
        "GetStartedForm.handleSubmit",
        "Form validation passed - Saving data and calling provider-search API",
        undefined,
        {},
      );
      setData("zip", value.zip);
      setData("ageRange", value.selectAge);
      setData("typeOfService", selectedServiceType);
      setData("providerType", getClinicianTypeForService(selectedServiceType));
      setData("paymentMethod", value.selectPlan);
      setData("selectedPolicy", value.selectedPolicy);
      setData("insurancePolicies", insurancePolicies);
      setData("isGetStartedChecked", hasSchedulingConsent);
      // Preserve "In-person" when the user has confirmed an in-person visit for an under-12 patient
      setData("sessionType", proceedAsInPersonRef.current ? "In-person" : persistedSessionType);
      setData("specialities", value.specialities);
      setData("specialityMatchType", specialityMatchType);

      // Under-12 users should follow legacy behavior (show modal for non-EAP)
      // and also show modal for EAP when role override is allowed.
      const isUserUnder12 = isUnder12AgeRange(value.selectAge);
      if (isUserUnder12 && !proceedAsInPersonRef.current && shouldShowUnder12AgeModal) {
        setIsLoading(false);
        handleUpdateAgeScenario();
        return;
      }

      const providerSearchAddress = resolveProviderSearchAddress(
        value.address,
        addressData || data.address,
      );
      if (!hasUsableAddress(providerSearchAddress)) {
        setIsLoading(false);
        setErrors((prev) => ({
          ...prev,
          zip: labels.errorAddressProcessing,
        }));
        return;
      }

      setData("careFormat", selectedCareFormat);
      setData("requiresGetStartedServiceType", shouldShowServiceType);
      const nextInsurancePolicy =
        value.selectPlan === "insurance"
          ? filteredInsurancePolicies.find(
              (val) => String(val.id) === String(value.selectedPolicy),
            ) || undefined
          : {};
      const hasExclusionNote =
        nextInsurancePolicy?.exclusion_note != null &&
        String(nextInsurancePolicy?.exclusion_note).trim() !== "";
      setData("insurancePolicy", nextInsurancePolicy || {});
      setData("userTimezone", timezone ?? data.userTimezone);

      // Publish Canvas outbound sync for Patient Navigator sessions.
      if (canvasIsInSalesforce && publishToSalesforce) {
        const outboundData = {
          ...data,
          zip: value.zip,
          ageRange: value.selectAge,
          paymentMethod: value.selectPlan,
          selectedPolicy: value.selectedPolicy,
          specialities: value.specialities,
          sessionType: persistedSessionType,
          careFormat: selectedCareFormat,
          insurancePolicy: nextInsurancePolicy,
        };
        publishToSalesforce(
          CANVAS_OUTBOUND_EVENT_NAME,
          buildOutboundPayload(outboundData, APP_ROUTES.GET_STARTED),
        );
      }

      /** Navigate to Contact page (skipping provider search) when:
       * - EAP plan: Contact shows EAP-specific message
       * - exclusion_ind true + exclusion note present: Contact shows the exclusion note
       * - exclusion_ind true + no exclusion note: Contact shows the default message
       * - User role is not allowed to override (roles_allowed_to_override): Contact shows the exclusion note or default message
       **/
      if (shouldNavigateToContact(nextInsurancePolicy, hasExclusionNote)) {
        setIsLoading(false);
        navigate(APP_ROUTES.CONTACT, {
          replace: false,
          state: {
            exclusionNote: nextInsurancePolicy?.exclusion_note,
            isEap,
          },
        });
        return;
      }

      const nextAppData = {
        ...data,
        ageRange: value.selectAge,
        providerType: getClinicianTypeForService(selectedServiceType),
        sessionType: persistedSessionType,
        specialities: value.specialities,
        specialityMatchType,
        careFormat: selectedCareFormat,
        address: providerSearchAddress,
        insurancePolicy: nextInsurancePolicy,
      };

      const requestedParams = buildProviderSearchRequest(nextAppData);
      const packageLookupReady = await ensureSelfPayPackageLookup({
        selectPlan: value.selectPlan,
        packageLookupCompleted,
        userUuid: data.userUuid,
        contextId: providerSearchAddress?.context_id,
        stateCode: providerSearchAddress?.state_code,
      });
      if (!packageLookupReady) {
        setIsLoading(false);
        return;
      }

      await retryApiCall(() => fetchProviderListOnce(requestedParams), {
        delay: 1000,
        retries: 3,
        onFailure: handleProviderSearchFailure,
        onSuccess: (providerList) => {
          return handleProviderSearchSuccess(
            providerList,
            proceedAsInPersonRef.current ? "in-person" : requestedParams.modality,
          );
        },
      });
    }); // end runExclusive
  };

  const reasonForVisitMultiSelectStyle = useMemo(
    () => ({
      ...sharedSelectInputStyle,
      "abyss-select-input-root": {
        ...sharedSelectInputStyle["abyss-select-input-root"],
        ".abyss-select-input-selection-count": {
          textOverflow: "unset !important",
        },
        ".abyss-select-input-selections": {
          whiteSpace: isMobile ? "pre-wrap !important" : "break-spaces !important",
          textOverflow: isMobile ? "clip !important" : "ellipsis !important",
          overflowWrap: "break-word !important",
          overflow: "hidden !important",
          pointerEvents: "auto",
          ".abyss-select-input-portal-container": {
            overflow: "visible !important",
            position: "relative !important",
          },
        },
      },
    }),
    [isMobile, sharedSelectInputStyle],
  );

  const insurancePolicySingleSelectStyle = useMemo(
    () => ({
      ...sharedSelectInputStyle,
      "abyss-select-input-root": {
        ...sharedSelectInputStyle["abyss-select-input-root"],
        ".abyss-select-input-selections": {
          whiteSpace: "unset",
          overflow: "hidden",
          pointerEvents: "auto",
        },
        div: {
          whiteSpace: isMobile ? "pre-wrap !important" : "break-spaces !important",
          textOverflow: isMobile ? "clip !important" : "ellipsis !important",
          overflowWrap: "break-word !important",
          overflow: "hidden !important",
          pointerEvents: "auto",
          ".abyss-select-input-portal-container": {
            overflow: "visible !important",
            position: "relative !important",
          },
        },
      },
    }),
    [isMobile, sharedSelectInputStyle],
  );

  const insurancePolicyOptions = Array.isArray(filteredInsurancePolicies)
    ? filteredInsurancePolicies.map((plan) => ({
        value: String(plan.id),
        label: getInsurancePackageDisplayName(plan, value.selectedEnrollmentCategory),
      }))
    : [];

  const allSpecialtyOptions = specialistOptions.map((specialty) => ({
    value: specialty,
    label: specialty,
  }));

  // Derive filtered lists from controlled input value state.
  const filteredSpecialtyOptions = moveSelectedToTop(
    filterBySearch(allSpecialtyOptions, specialtyInputValue),
    value.specialities,
  );
  const filteredPolicyOptions = moveSelectedToTop(
    filterBySearch(insurancePolicyOptions, policyInputValue),
    toSelectedPolicyValues(value.selectedPolicy),
  );

  return (
    <Fragment>
      {showAgeRangeResetConfirmation && (
        <UpdateAgeModal
          isOpen={showAgeRangeResetConfirmation}
          text="Please re-enter the correct age range before continuing."
          variant="age_range_reset_confirmation"
          onClose={() => setShowAgeRangeResetConfirmation(false)}
        />
      )}
      {shouldShowUnder12AgeModal && (
        <UpdateAgeModal
          isOpen={showUpdateAgeModal}
          onClose={handleUpdateAgeModalClose}
          onConfirm={handleUpdateAgeModalConfirm}
          text="Patients under 12 must be seen in-person rather than via a virtual visit. You may proceed to book an in-person visit, or click Update age to correct the date of birth you have provided."
          variant="in_person_visit"
        />
      )}
      {/* Loading Overlay for form validation and provider search */}
      <LoadingOverlay isLoading={isLoading || canvasIsLoading} />
      <LocalCenterProviderUnavailableModal
        isOpen={showProviderNotFoundModal}
        onSeeOtherProviders={handleProviderNotFoundSeeMoreProviders}
      />
      <form
        id="get-started-form"
        data-testid="test-id-get-started-form"
        onSubmit={(e) => handleSubmit(e)}
        style={{ width: "stretch" }}
      >
        <Card
          data-testid="test-id-get-started-card"
          className={isMobile ? "v2card-form-mobile-style" : ""}
          css={{
            width: "100%",
            padding: "2rem",
          }}
        >
          <FormSpacing>
            {isFeaturePrescriberEnabled && (
              <SelectInput
                data-testid="test-id-get-started-type-of-service-select"
                label="Type of service desired"
                value={value.typeOfService}
                onChange={(selectedValue) => handleChange("typeOfService", selectedValue)}
                isRequired
                options={ServiceTypeOptions.map((serviceType) => ({
                  value: serviceType,
                  label: serviceType,
                }))}
                errorMessage={errors.typeOfService}
                aria-label="Type of service desired"
                id="type-of-service-select"
              />
            )}

            <TextInput
              data-testid="test-id-get-started-zip-input"
              label={labels.labelZip}
              autoComplete="postal-code"
              value={value.zip}
              onChange={(e) => handleChange("zip", e.target.value)}
              isRequired
              errorMessage={errors.zip}
              aria-label={labels.ariaZip}
              id="zip-input"
            />

            <AgeRangeSelectWrapper>
              <SelectInput
                data-testid="test-id-get-started-age-range-select"
                label={
                  (
                    <AgeRangeGuidance>
                      {labels.labelAgeRange}
                      <AgeRangeRequiredIndicator aria-hidden="true">*</AgeRangeRequiredIndicator>
                      <Tooltip
                        content={labels.ageRangeGuidance}
                        aria-label="Age range and date of birth guidance"
                        data-testid="test-id-get-started-age-range-guidance"
                      >
                        <AgeRangeGuidanceIcon>
                          <IconSymbol icon="info" size="16" />
                        </AgeRangeGuidanceIcon>
                      </Tooltip>
                    </AgeRangeGuidance>
                  ) as unknown as string
                }
                value={value.selectAge}
                onChange={(selectedValue) => handleChange("selectAge", selectedValue)}
                isRequired
                options={[
                  ...ageRangeOptions.map((ageRange) => ({
                    value: ageRange,
                    label: ageRange,
                  })),
                ]}
                errorMessage={errors.selectAge}
                aria-label={labels.ariaAgeRange}
                id="age-range-select"
              />
            </AgeRangeSelectWrapper>

            <SessionReasonGroup
              data-testid="test-id-get-started-reason-for-visit-container"
              ref={specialtyContainerRef}
            >
              {isSchedulingConsolidationEnabled && (
                <SelectInput
                  label="Session type"
                  data-testid="test-id-session-type-select"
                  value={value.sessionType}
                  onChange={(selectedOptions) => handleChange("sessionType", selectedOptions || [])}
                  isMulti
                  isRequired
                  options={SessionTypeOptions.map((sessionType) => ({
                    value: sessionType,
                    label: sessionType,
                    isDisabled:
                      (proceedAsInPersonRef.current || isUnder12AgeRange(value.selectAge)) &&
                      sessionType === "Virtual",
                  }))}
                  errorMessage={errors.sessionType}
                  aria-label="Session type"
                  id="session-type-select"
                />
              )}
              <SelectInput
                data-testid="test-id-get-started-reason-for-visit-select"
                key={`specialty-${specialtyResetKey}`}
                label="Reason for visit"
                isClearable
                isSearchable
                isMulti
                value={value.specialities}
                onChange={(selectedOptions) => {
                  setSpecialtyInputValue("");
                  setSpecialtyResetKey((k) => k + 1);
                  handleChange("specialities", selectedOptions || []);
                }}
                enableOutsideScroll={canvasIsInSalesforce}
                options={filteredSpecialtyOptions}
                searchConfig={false}
                onInputChange={(text: string) => {
                  setSpecialtyInputValue(text);
                }}
                css={reasonForVisitMultiSelectStyle}
                errorMessage={reasonsForVisitError}
                aria-label="Select reason for visit"
                id="reason-for-visit-select"
              />
              {value.specialities.length > 1 && (
                <SpecialityMatchTypeToggle
                  testIdPrefix="test-id-get-started"
                  value={specialityMatchType}
                  onChange={setSpecialityMatchType}
                />
              )}
            </SessionReasonGroup>

            {/* Product labels careFormat (Individual/Couples/Family) as "Service type". */}
            {shouldShowServiceType && (
              <SelectInput
                data-testid="test-id-get-started-service-type-select"
                label="Service type"
                value={value.careFormat}
                onChange={(selectedValue) => handleChange("careFormat", selectedValue)}
                isRequired
                options={CareFormatOptions.map((careFormat) => ({
                  value: careFormat,
                  label: careFormat,
                }))}
                errorMessage={errors.careFormat}
                aria-label="Service type"
                id="service-type-select"
              />
            )}

            <SelectInput
              data-testid="test-id-get-started-payment-method-select"
              label={labels.labelPaymentMethod}
              value={value.selectPlan}
              onChange={(selectedOption) => {
                return handleChange("selectPlan", selectedOption);
              }}
              isRequired
              options={PaymentMethodOptions}
              errorMessage={errors.selectPlan}
              aria-label={labels.ariaPaymentMethod}
              id="payment-method-select"
            />
            <InsurancePackageSelectionGroup
              ref={policyContainerRef}
              data-testid="test-id-get-started-insurance-policy-container"
            >
              {isEnrollmentCategoryPackageFilterEnabled && (
                <SelectInput
                  data-testid="test-id-get-started-enrollment-category-select"
                  key={`enrollment-category-${enrollmentCategoryResetKey}`}
                  label="Insurance category"
                  value={value.selectedEnrollmentCategory || ""}
                  onChange={(selectedOption) =>
                    handleChange("selectEnrollmentCategory", selectedOption)
                  }
                  isRequired
                  options={enrollmentCategoryOptions}
                  errorMessage={errors.selectedEnrollmentCategory}
                  aria-label="Insurance category"
                  id="enrollment-category-select"
                  isDisabled={
                    value.selectPlan !== "insurance" || enrollmentCategoryOptions.length === 0
                  }
                />
              )}
              <SelectInput
                data-testid="test-id-get-started-insurance-policy-select"
                key={`policy-${policyResetKey}`}
                label={labels.labelInsurancePolicy}
                value={value.selectedPolicy}
                onChange={(selectedOption) => {
                  setPolicyInputValue("");
                  setPolicyResetKey((k) => k + 1);
                  handleChange("selectPolicy", selectedOption);
                }}
                enableOutsideScroll={canvasIsInSalesforce}
                isRequired
                options={filteredPolicyOptions}
                errorMessage={errors.selectedPolicy}
                aria-label={labels.ariaInsurancePolicy}
                id="insurance-policy-select"
                isDisabled={
                  value.selectPlan === "self_pay" ||
                  (isEnrollmentCategoryPackageFilterEnabled && !value.selectedEnrollmentCategory) ||
                  insurancePolicyOptions.length === 0
                }
                isSearchable
                searchConfig={false}
                onInputChange={(text: string) => {
                  setPolicyInputValue(text);
                }}
                css={insurancePolicySingleSelectStyle}
              />
            </InsurancePackageSelectionGroup>
          </FormSpacing>
        </Card>
        {!isPatientNavigator && (
          <FormFooter
            data-testid="test-id-get-started-form-footer"
            isChecked={isChecked}
            setIsChecked={setIsChecked}
            showError={showCheckboxError}
            isMobile={isMobile}
            ageRange={value.selectAge}
          />
        )}
      </form>
    </Fragment>
  );
};
